import { useParams } from "@tanstack/react-router";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import { AddSourceCard } from "@/components/intake/add-source-card";
import { BaselineGateBanner } from "@/components/intake/baseline-gate-banner";
import { ProcessSourceProgressDialog } from "@/components/intake/process-source-progress-dialog";
import { SourceDetailSheet } from "@/components/intake/source-detail-sheet";
import { SourcesList } from "@/components/intake/sources-list";
import { SourcesSummaryStrip } from "@/components/intake/sources-summary-strip";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSolution } from "@/hooks/use-solutions";
import { useProcessSource, useSources } from "@/hooks/use-sources";
import { useStakeholders } from "@/hooks/use-stakeholders";

const SKELETON_KEYS = ["a", "b", "c", "d"] as const;

export function IntakePage() {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const sourcesQuery = useSources(solutionId);
  const stakeholdersQuery = useStakeholders(solutionId);
  const solution = useSolution(solutionId);
  const processMutation = useProcessSource(solutionId);
  const [openSourceId, setOpenSourceId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [progressSourceId, setProgressSourceId] = useState<string | null>(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);

  const sources = sourcesQuery.data;
  const sourcesCount = sources?.length ?? 0;
  const isFirstSource = !sourcesQuery.isLoading && sourcesCount === 0;
  const showAddCard = isFirstSource || addOpen;
  const baselineConfirmed = Boolean(solution.data?.baselineConfirmedAt);

  // Track which source(s) have an in-flight inline mutation so the row can
  // show "Processing…" immediately on click, even before the list invalidates.
  const inFlightSourceId =
    processMutation.isPending && processMutation.variables
      ? processMutation.variables.sourceId
      : null;

  const progressSource = progressSourceId
    ? (sources?.find((s) => s.id === progressSourceId) ?? null)
    : null;

  const handleInlineProcess = async (sourceId: string) => {
    setRunId(null);
    setProgressSourceId(sourceId);
    setProgressOpen(true);
    try {
      await processMutation.mutateAsync({
        sourceId,
        reprocess: false,
        onRunStarted: setRunId,
      });
    } catch {
      // Errors are surfaced via toast in the mutation's onError handler.
    } finally {
      setProgressOpen(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 space-y-6">
      <BaselineGateBanner solutionId={solutionId} />
      <PageHeader
        title="Intake"
        description="Add transcripts, docs, surveys, or paste raw text. The AI extracts requirement candidates that show up in Review."
        actions={
          !isFirstSource && !addOpen ? (
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" />
              Add source
            </Button>
          ) : undefined
        }
      />

      {showAddCard && (
        <div className="space-y-2">
          {!isFirstSource && (
            <div className="flex items-center justify-between">
              <h2 className="font-display text-sm font-semibold">Add a source</h2>
              <Button variant="ghost" size="sm" onClick={() => setAddOpen(false)} title="Close">
                <X className="h-3.5 w-3.5 mr-1.5" />
                Cancel
              </Button>
            </div>
          )}
          <AddSourceCard
            solutionId={solutionId}
            onAdded={() => {
              if (!isFirstSource) setAddOpen(false);
            }}
          />
        </div>
      )}

      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-sm font-semibold">Sources</h2>
          {sources && sources.length > 0 && <SourcesSummaryStrip sources={sources} />}
        </div>

        {sourcesQuery.isLoading && (
          <ul className="space-y-2">
            {SKELETON_KEYS.map((k) => (
              <Skeleton key={k} className="h-[68px] rounded-lg" />
            ))}
          </ul>
        )}

        {sourcesQuery.isError && (
          <ErrorState message="Failed to load sources" onRetry={() => sourcesQuery.refetch()} />
        )}

        {sources && sources.length > 0 && (
          <SourcesList
            sources={sources}
            stakeholders={stakeholdersQuery.data}
            baselineConfirmed={baselineConfirmed}
            isProcessing={(id) => id === inFlightSourceId}
            onSelectSource={setOpenSourceId}
            onProcessClick={handleInlineProcess}
          />
        )}
      </section>

      <SourceDetailSheet
        sourceId={openSourceId}
        solutionId={solutionId}
        stakeholders={stakeholdersQuery.data}
        onOpenChange={(open) => {
          if (!open) setOpenSourceId(null);
        }}
      />

      <ProcessSourceProgressDialog
        open={progressOpen}
        sourceName={progressSource?.name ?? ""}
        sourceByteLength={progressSource?.rawText?.length ?? 0}
        runId={runId}
        onCancel={() => setProgressOpen(false)}
      />
    </div>
  );
}
